import React, { useCallback, useEffect, useRef, useState } from 'react';
import LabelEventGraph from './LabelEventGraph';
import {
  LABEL_TYPES,
  GeoIcon,
  COULEURS_PHASES,
  LabelHoverCard,
  LabelEditDialog,
  PhaseEditDialog,
} from './LabelComponents';
import Bouton from '../../../UI/Bouton/Bouton';
import * as type_btn from '../../../../ressources/glossaires/glossaire_type_de_bouton';
import * as glossaire_type_de_graphe from '../../../../ressources/glossaires/glossaire_type_de_graphe';
import { fetchGraphData } from './labelEventGraphApi';

import {
  getLabels,
  createLabel,
  deleteLabel,
  updateLabel,
  getPhases,
  createPhase,
  updatePhase,
  deletePhase,
} from '../../../../services/api';

// ── Constantes ────────────────────────────────────────────────────────────────

const GRAPH_ID = 1;
const HEADER_HEIGHT = 20;
const PADDING = 8;

// ── Mapping API ↔ format interne ──────────────────────────────────────────────

const mapApiLabel = (l) => ({
  id: l.id,
  t: l.t,
  label: l.label,
  note: l.note || '',
  labelType: l.label_type,
  iconType: l.icon_type,
});

const mapApiSection = (s) => ({
  id: s.id,
  tStart: s.t_start,
  tEnd: s.t_end,
  label: s.label,
  color: s.color,
});

const parseSensorPositionFormula = (formula) => {
  const [sensorId, position] = formula
    .split(';')[0] // prend le premier bloc (ou le seul)
    .match(/\d+/g)
    .map(Number);
  return { sensorId, position };
};

const mapDraftToApiPayload = (draft) => {
  const { sensorId, position } = parseSensorPositionFormula(
    draft.sensorAndPosition
  );
  return {
    id_sensor: sensorId,
    position_in_sensor: position,
    time_unix: draft.t,
    label_text: draft.label ?? '',
    source: 'ui', // ou 'manual' selon ton backend
  };
};

// ── Composant principal ───────────────────────────────────────────────────────

const LabelEventGraphGeoDemo = () => {
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const timerEtiquette = useRef(null);
  const prochainIdPhase = useRef(0);

  const [dims, setDims] = useState({ width: undefined, height: undefined });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [signalData, setSignalData] = useState([]);
  const [graphConfig, setGraphConfig] = useState({});
  const [dateOptions, setDateOptions] = useState([]);
  const [graphCurves, setGraphCurves] = useState([]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [creatingLabel, setCreatingLabel] = useState(null);
  const [editingPhase, setEditingPhase] = useState(null);
  const [creatingPhase, setCreatingPhase] = useState(null);

  const [labels, setLabels] = useState([]);
  const [phases, setPhases] = useState([]);

  // ── Chargement initial ────────────────────────────────────────────────────────

  useEffect(() => {
    try {
      getPhases({ graphId: GRAPH_ID }).then((res) => {
        if (res.ok) setPhases(res.data.map(mapApiSection));
      });
      fetchGraphData(GRAPH_ID).then((res) => {
        if (!res.ok) {
          setFetchError(res.error);
          setLoading(false);
          return;
        }
        const {
          config,
          data,
          graphCurves,
          labels: apiLabels,
          sections: apiSections,
          id_sensor,
        } = res.data;
        setSignalData(data);
        setGraphConfig(config);
        const mappedSections = apiSections.map(mapApiSection);
        prochainIdPhase.current = mappedSections.length;
        // Construire les options de dates à partir de la courbe signal
        const tArr = data[0]?.x || [];
        const yArr = data[0]?.y || [];
        id_sensor.map((sensorId) => {
          getLabels({ sensorId }).then((res) => {
            if (res.ok) {
              const mapped = res.data.map(mapApiLabel);
              setLabels((prev) => [...prev, ...mapped]);
            }
          });
        });
        setDateOptions(
          tArr
            .map((ts, i) => ({ ts, y: yArr[i] }))
            .filter((_, i) => i % 2 === 0)
        );
        setGraphCurves(graphCurves);
        setLoading(false);
      });
    } catch (err) {
      console.error('Erreur lors du chargement des données', err);
      setFetchError('Impossible de charger les données du graphe');
      setLoading(false);
    }
  }, []);

  // ── Redimensionnement ─────────────────────────────────────────────────────────

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setDims({
          width: containerRef.current.offsetWidth - 28,
          height: window.innerHeight - HEADER_HEIGHT - PADDING,
        });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // ── Fermer le menu au clic extérieur ─────────────────────────────────────────

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [menuOpen]);

  // ── Rappels — étiquettes ──────────────────────────────────────────────────────

  const addLabel = useCallback(
    (type = 'JALON') => {
      if (!dateOptions.length) return;
      const labelConfig = LABEL_TYPES[type];
      const midT = dateOptions[Math.floor(dateOptions.length / 2)].ts;
      setCreatingLabel({
        id: null,
        t: midT,
        iconType: labelConfig.iconType,
        labelType: type,
        label: '',
        note: '',
      });
    },
    [dateOptions]
  );

  const handleAddLabelFromSignal = useCallback(
    ({ ts, yVal }) => {
      if (!dateOptions.length) return;
      const closest = dateOptions.reduce((prev, curr) =>
        Math.abs(curr.ts - ts) < Math.abs(prev.ts - ts) ? curr : prev
      );
      setCreatingLabel({
        id: null,
        t: closest.ts,
        iconType: LABEL_TYPES.JALON.iconType,
        labelType: 'JALON',
        label: '',
        note:
          yVal !== undefined ? `Valeur : ${Math.round(yVal * 10) / 10} dB` : '',
      });
    },
    [dateOptions]
  );

  const handleSaveLabel = async (draft) => {
    try {
      const payload = mapDraftToApiPayload(draft);

      let savedLabel;

      if (draft.id == null) {
        // ── création ─────────────────────────────
        const res = await createLabel(payload);
        savedLabel = res.data; // adapte selon ton API
      } else {
        // ── mise à jour ──────────────────────────
        const res = await updateLabel({
          labelId: draft.id,
          ...payload,
        });
        savedLabel = res.data;
      }

      // ── sync état local ─────────────────────────
      setLabels((prev) => {
        if (draft.id == null) {
          return [...prev, mapApiLabel(savedLabel)];
        }
        return prev.map((l) =>
          l.id === savedLabel.id ? mapApiLabel(savedLabel) : l
        );
      });

      setEditingLabel(null); // ou setCreatingLabel(null)
    } catch (err) {
      console.error('Erreur sauvegarde label', err);
      alert("Impossible d'enregistrer le label");
    }
  };

  const handleRemoveLabel = useCallback(async () => {
    if (!hoveredLabel) return;
    const { id } = hoveredLabel.label;
    const res = await deleteLabel({ labelId: id });
    if (res.ok) setLabels((prev) => prev.filter((l) => l.id !== id));
    setHoveredLabel(null);
  }, [hoveredLabel]);

  // ── Rappels — phases ──────────────────────────────────────────────────────────

  const ajouterPhase = useCallback(() => {
    const color =
      COULEURS_PHASES[prochainIdPhase.current % COULEURS_PHASES.length];
    const tArr = dateOptions.map((d) => d.ts);
    setCreatingPhase({
      id: null,
      tStart: tArr[0] || 0,
      tEnd: tArr[tArr.length - 1] || 0,
      label: '',
      color,
    });
  }, [dateOptions]);

  const handleSavePhase = useCallback(async (draft) => {
    const payload = {
      time_start: draft.tStart,
      time_end: draft.tEnd,
      phase_text: draft.label ?? '',
      color_hex: draft.color ?? '#888888',
    };

    const res =
      draft.id == null
        ? await createPhase({
            graph_id: GRAPH_ID,
            ...payload,
          })
        : await updatePhase({
            graph_id: GRAPH_ID,
            ...payload,
          });

    if (res.ok) {
      setPhases((prev) => {
        const mapped = mapApiSection(res.data);

        return draft.id == null
          ? [...prev, mapped]
          : prev.map((p) => (p.id === draft.id ? mapped : p));
      });
    }

    setCreatingPhase(null);
    setEditingPhase(null);
  }, []);

  const handleDeletePhase = useCallback(async (draft) => {
    const res = await deletePhase({ phaseId: draft.id });
    if (res.ok) setPhases((prev) => prev.filter((s) => s.id !== draft.id));
    setEditingPhase(null);
  }, []);

  const handlePhaseClick = useCallback(
    (phaseData) => {
      const found = phases.find(
        (s) => s.tStart === phaseData.tStart && s.tEnd === phaseData.tEnd
      );
      if (found) setEditingPhase({ ...found });
    },
    [phases]
  );

  // ── Rappels survol — étiquettes ───────────────────────────────────────────────

  const handleLabelHover = useCallback(
    (t, x, y) => {
      const found = labels.find((l) => l.t === t);
      if (found) setHoveredLabel({ label: found, x, y });
    },
    [labels]
  );

  const handleLabelUnhover = useCallback(() => {
    timerEtiquette.current = setTimeout(() => setHoveredLabel(null), 150);
  }, []);

  const handlePencilEdit = useCallback(() => {
    if (hoveredLabel) setEditingLabel({ ...hoveredLabel.label });
  }, [hoveredLabel]);

  // ── Configuration du graphe ───────────────────────────────────────────────────

  const configuration = {
    title: graphConfig.title || '',
    subtitle: graphConfig.subtitle || '',
    xaxis: graphConfig.xaxis || {},
    yaxis: graphConfig.yaxis || {},
    labels: labels.map(({ id, labelType, ...rest }) => rest),
    sections: phases.map(({ id, ...rest }) => rest),
  };

  const donneesGraphe = {
    config: {
      graph_type_id: glossaire_type_de_graphe.label_event_graph_geo,
      title: graphConfig.title || '',
      sensor_name: graphConfig.sensor_name || '',
      prefix: graphConfig.prefix || '',
    },
    data: signalData,
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────────

  if (loading)
    return (
      <div
        className="leg-demo-root"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        Chargement…
      </div>
    );

  if (fetchError)
    return (
      <div className="leg-demo-root" style={{ color: 'red', padding: 24 }}>
        Erreur : {fetchError}
      </div>
    );

  return (
    <div ref={containerRef} className="leg-demo-root">
      <div className="leg-demo-graph-panel">
        <div className="leg-demo-panel-title">
          <span>Démo — LabelEventGraph (Labels IA et Jalons)</span>
          <div ref={menuRef} className="leg-demo-menu-wrapper">
            <Bouton
              type={type_btn.succes}
              cliquer={() => setMenuOpen((o) => !o)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '6px 14px 6px 10px',
                height: '34px',
                borderRadius: '6px',
                flexShrink: 0,
              }}
              title="Ajouter"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                className="leg-demo-btn-svg"
              >
                <path
                  fill="black"
                  d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zm0 4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm6.07-1.18a6.15 6.15 0 0 0 .05-.82c0-.28-.02-.55-.06-.82l1.77-1.38a.42.42 0 0 0 .1-.54l-1.68-2.9a.42.42 0 0 0-.52-.18l-2.08.84a6.1 6.1 0 0 0-1.41-.82L9.9.5A.42.42 0 0 0 9.5.1h-3a.42.42 0 0 0-.41.35l-.3 2.22a6.1 6.1 0 0 0-1.41.82L2.3 2.65a.42.42 0 0 0-.52.18L.1 5.74a.41.41 0 0 0 .1.54l1.77 1.38A6.3 6.3 0 0 0 1.9 8.5c0 .27.02.54.06.8L.19 10.7a.42.42 0 0 0-.1.54l1.68 2.9c.1.18.33.25.52.18l2.08-.83c.44.31.91.57 1.41.81l.3 2.22c.05.22.25.38.48.38h3.36c.23 0 .43-.16.48-.38l.3-2.22a6.1 6.1 0 0 0 1.41-.81l2.08.83c.2.07.42 0 .52-.18l1.68-2.9a.42.42 0 0 0-.1-.54l-1.77-1.4zm-1.01 2.17-.6-.24-.24-.1-1.73.7-.22 1.63H5.73l-.22-1.63-1.73-.7-.84.34-1.25-2.16 1.47-1.14-.06-.5A5.2 5.2 0 0 1 2.9 8.5c0-.22.01-.44.04-.66l.06-.5-1.47-1.14 1.25-2.16 2.05.82 1.73-.7L6.78 2.5h2.44l.22 1.66 1.73.7 2.05-.82 1.25 2.16-1.47 1.14.06.5c.03.22.04.44.04.66 0 .22-.01.44-.04.66l-.06.5 1.47 1.14-1.25 2.16z"
                />
              </svg>
              <span className="leg-demo-btn-label">Ajouter</span>
            </Bouton>
            {menuOpen && (
              <div className="leg-dropdown-menu">
                <button
                  className="leg-dropdown-item"
                  onClick={() => {
                    addLabel('JALON');
                    setMenuOpen(false);
                  }}
                >
                  <GeoIcon iconType="diamond" size={12} />
                  <span>Jalon</span>
                </button>
                <div className="leg-dropdown-divider" />
                <button
                  className="leg-dropdown-item"
                  onClick={() => {
                    ajouterPhase();
                    setMenuOpen(false);
                  }}
                >
                  <span>▬</span>
                  <span>Phase</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <LabelEventGraph
          donnees={signalData}
          configuration={configuration}
          donneesGraphe={donneesGraphe}
          dimensionWidth={dims.width}
          dimensionHeight={dims.height ? dims.height - 40 : undefined}
          switchAfficherModifierIntervalle={() => {}}
          viewobj_json_content={{ displayModeBar: true }}
          onLabelHover={handleLabelHover}
          onLabelUnhover={handleLabelUnhover}
          onPhaseClick={handlePhaseClick}
          onAddLabel={handleAddLabelFromSignal}
        />
      </div>

      {hoveredLabel && !editingLabel && (
        <LabelHoverCard
          labelData={hoveredLabel.label}
          x={hoveredLabel.x}
          y={hoveredLabel.y}
          onEdit={handlePencilEdit}
          onRemove={handleRemoveLabel}
          onMouseEnter={() => clearTimeout(timerEtiquette.current)}
          onMouseLeave={() => setHoveredLabel(null)}
        />
      )}

      {editingLabel && (
        <LabelEditDialog
          label={editingLabel}
          dateOptions={dateOptions}
          sensorsPositions={graphCurves}
          data={signalData}
          onSave={handleSaveLabel}
          onCancel={() => setEditingLabel(null)}
        />
      )}

      {creatingLabel && (
        <LabelEditDialog
          label={creatingLabel}
          dateOptions={dateOptions}
          data={signalData}
          sensorsPositions={graphCurves}
          onSave={handleSaveLabel}
          onCancel={() => setCreatingLabel(null)}
        />
      )}

      {editingPhase && (
        <PhaseEditDialog
          phase={editingPhase}
          onSave={handleSavePhase}
          onCancel={() => setEditingPhase(null)}
          onDelete={handleDeletePhase}
        />
      )}

      {creatingPhase && (
        <PhaseEditDialog
          phase={creatingPhase}
          onSave={handleSavePhase}
          onCancel={() => setCreatingPhase(null)}
        />
      )}
    </div>
  );
};

export default LabelEventGraphGeoDemo;
